import * as React from 'react';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import UserTagRecalculationStatus from './UserTagRecalculationStatus';

const humanizeEnum = (value: string) => value
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

interface UserTagCardProps {
    tag: UserTagDefinitionDto;
    onEdit?: (tagId: string) => void;
    onViewUsers?: (tagName: string) => void;
    onDelete?: (tag: UserTagDefinitionDto) => Promise<boolean>;
}

export default function UserTagCard({ tag, onEdit, onViewUsers, onDelete }: UserTagCardProps) {
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);

    const confirmDelete = async () => {
        if (!onDelete) return;
        setDeleting(true);
        const deleted = await onDelete(tag);
        setDeleting(false);
        if (deleted) {
            setDeleteConfirmationOpen(false);
        }
    };

    return (
        <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                <Stack gap={0.25}>
                    <Typography gutterBottom variant="h6" component="div" sx={{ mb: 0, overflowWrap: 'anywhere' }}>
                        {tag.tagName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {tag.approximateUsersCount == null
                            ? 'Approx. users: counting...'
                            : `Approx. users: ${tag.approximateUsersCount.toLocaleString()}`}
                    </Typography>
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {tag.requirements.map(requirement => (
                        <Chip
                            key={requirement.type}
                            label={humanizeEnum(requirement.type)}
                            size="small"
                            variant="outlined"
                        />
                    ))}
                </Stack>
                <UserTagRecalculationStatus tag={tag} compact />
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2, justifyContent: 'flex-end' }}>
                {onViewUsers && (
                    <Button size="small" variant="text" onClick={() => onViewUsers(tag.tagName)}>
                        View users
                    </Button>
                )}
                {onEdit && (
                    <Button size="small" variant="outlined" onClick={() => onEdit(tag.id)}>
                        Edit
                    </Button>
                )}
                {onDelete && (
                    <Button size="small" color="error" variant="outlined" onClick={() => setDeleteConfirmationOpen(true)}>
                        Delete
                    </Button>
                )}
            </CardActions>
            <Dialog open={deleteConfirmationOpen} onClose={() => !deleting && setDeleteConfirmationOpen(false)}>
                <DialogTitle>Delete user tag?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Delete "{tag.tagName}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmationOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
